import React, { useEffect, useState } from 'react';

import { Flex, Paper } from '@mantine/core';
import { IconMessages } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import 'dayjs/locale/fr';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { ConversationList } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationList';
import { IndividualConversationView } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationsSection/IndividualConversationView';
import { ExportButton } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ExportButton';
import { FilterControls } from 'pages/ManageSmartAssistant/Sections/ConversationSections/FilterControls';
import { useTranslation } from 'react-i18next';
import { ConversationService } from 'services/conversation-service';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { NotificationService } from 'services/notification-service';
import { EXPORT_TYPE_TO_MIME_TYPE, ExportType } from 'shared/constants/export';
import { SMART_ASSISTANT_CONVERSATIONS_QUERY_KEY } from 'shared/constants/query-keys';
import { Conversation, ExportConversationsDto, HandoffStatus, TakeoverStatus } from 'shared/interfaces/Conversation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import {
  SMART_ASSISTANT_TYPES_CONVERSATION_FILTERS,
  getSourceTypeSelectDataBySmartAssistantType
} from 'shared/utils/conversation';
import { DAY_IN_MILLISECONDS, DEFAULT_DATE_RANGE, DateRangePickerValue } from 'shared/utils/date';

interface ConversationsSectionProps {
  smartAssistant: SmartAssistant;
  forcedConversationId?: string;
  isGuest?: boolean;
}

export function ConversationsSection({
  smartAssistant,
  forcedConversationId,
  isGuest = false
}: ConversationsSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();

  const [dateRange, setDateRange] = useState<DateRangePickerValue>(DEFAULT_DATE_RANGE);
  const [sourceTypes, setSourceTypes] = useState(
    getSourceTypeSelectDataBySmartAssistantType(smartAssistant.type).map((item) => item.value) as string[]
  );
  const [handoffStatus, setHandoffStatus] = useState(HandoffStatus.ALL);
  const [takeoverStatus, setTakeoverStatus] = useState(TakeoverStatus.ALL);
  const [currentConversation, setCurrentConversation] = useState<Conversation>();
  const [displayNoConversation, setDisplayNoConversation] = useState(false);
  const [forcedConversationShowed, setForcedConversationShowed] = useState(false);

  const getConversationQuery = () => {
    const query: { [key: string]: string[] | number | string } = {};
    if (!forcedConversationShowed && forcedConversationId) query['forcedConversationId'] = forcedConversationId;
    if (sourceTypes.length > 0) query['sourceTypes'] = sourceTypes;
    if (handoffStatus !== HandoffStatus.ALL) query['handoffStatus'] = handoffStatus;
    if (takeoverStatus !== TakeoverStatus.ALL) query['takeoverStatus'] = takeoverStatus;
    if (!dateRange || !dateRange[0] || !dateRange[1]) return query;
    else return { ...query, start: dateRange[0].getTime(), end: dateRange[1].getTime() + DAY_IN_MILLISECONDS - 1 };
  };

  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({
    smartAssistant,
    avatarUrl: smartAssistant.avatarUrl
  });

  const smartAssistantConversationsQuery = useQuery({
    queryKey: [SMART_ASSISTANT_CONVERSATIONS_QUERY_KEY, { smartAssistantId: smartAssistant._id }],
    queryFn: () =>
      (isGuest ? GuestSmartAssistantService : ConversationService).getAllSmartAssistantConversations(
        smartAssistant._id,
        getConversationQuery()
      ),
    onSuccess: (conversations: Conversation[]) => {
      setDisplayNoConversation(conversations.length === 0);
      const alreadySelectedConversation = currentConversation
        ? conversations.find((conversation) => conversation._id === currentConversation._id)
        : undefined;

      alreadySelectedConversation
        ? setCurrentConversation(alreadySelectedConversation)
        : setCurrentConversation(conversations.length > 0 ? conversations[0] : undefined);
    },
    enabled: subscription.product.enableConversationAnalytics || isGuest
  });

  const exportConversationsMutation = useMutation({
    mutationFn: (isGuest ? GuestSmartAssistantService : ConversationService).exportConversations,
    onSuccess: (buffer: ArrayBuffer, arg: ExportConversationsDto) => {
      const mimeType = EXPORT_TYPE_TO_MIME_TYPE.get(arg.type);
      const blob = new Blob([buffer], { type: mimeType });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        t('SMART_ASSISTANT.CONVERSATIONS_SECTION.EXPORT.FILE_NAME', { fileExtension: arg.type })
      );
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.CONVERSATION.FAILURE.EXPORT')
  });

  useEffect(() => {
    if (!dateRange) return;
    // If one of the date is not set, we don't want to refetch
    if ((!dateRange[0] && dateRange[1]) || (dateRange[0] && !dateRange[1])) return;
    (subscription.product.enableConversationAnalytics || isGuest) && smartAssistantConversationsQuery.refetch();
  }, [dateRange, sourceTypes, handoffStatus, takeoverStatus]);

  if (smartAssistantConversationsQuery.isError) return <ErrorAlert />;

  const exportConversations = (type: ExportType) => {
    exportConversationsMutation.mutate({
      smartAssistantId: smartAssistant._id,
      type,
      query: getConversationQuery()
    });
  };

  const onConversationUpdate = (updatedConversation: Conversation) => {
    queryClient.setQueryData(
      [SMART_ASSISTANT_CONVERSATIONS_QUERY_KEY, { smartAssistantId: smartAssistant._id }],
      (existingConversations: Conversation[] = []) =>
        existingConversations.map((conversation) =>
          conversation._id === updatedConversation._id ? updatedConversation : conversation
        )
    );
    if (currentConversation?._id === updatedConversation._id) setCurrentConversation(updatedConversation);
  };

  const conversations = smartAssistantConversationsQuery.data ?? [];

  return (
    <ManageSmartAssistantSectionControlWrapper title={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TITLE')}>
      <UpgradePlanBlurCard
        message={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.UPGRADE.DESCRIPTION')}
        showCard={!(subscription.product.enableConversationAnalytics || isGuest)}>
        <Flex direction="row" align="flex-end" justify="space-between" gap="lg" mt={5} mb={15}>
          <FilterControls
            smartAssistant={smartAssistant}
            visibleFilters={SMART_ASSISTANT_TYPES_CONVERSATION_FILTERS[smartAssistant.type]}
            dateRange={dateRange}
            setDateRange={setDateRange}
            handoffStatus={handoffStatus}
            setHandoffStatus={setHandoffStatus}
            takeoverStatus={takeoverStatus}
            setTakeoverStatus={setTakeoverStatus}
            sourceTypes={sourceTypes}
            setSourceTypes={setSourceTypes}
            setForcedConversationShowed={setForcedConversationShowed}
          />
          <ExportButton
            disabled={conversations.length === 0}
            loading={exportConversationsMutation.isLoading}
            performExport={exportConversations}
          />
        </Flex>

        <Paper withBorder h="100%" w="100%" p="xs" style={{ flexGrow: 1, flexShrink: 1, overflow: 'hidden' }}>
          <Flex h="100%">
            {displayNoConversation ? (
              <NoDataFound
                h="100%"
                w="100%"
                icon={<IconMessages />}
                title={t('NO_DATA_FOUND.CONVERSATIONS.TITLE')}
                subTitle={t('NO_DATA_FOUND.CONVERSATIONS.SUB_TITLE')}
              />
            ) : (
              <>
                <ConversationList
                  conversations={conversations}
                  currentConversation={currentConversation}
                  setCurrentConversation={setCurrentConversation}
                  forcedConversationId={forcedConversationId}
                  forcedConversationShowed={forcedConversationShowed}
                  setForcedConversationShowed={setForcedConversationShowed}
                />
                <IndividualConversationView
                  conversation={currentConversation}
                  smartAssistant={smartAssistant}
                  smartAssistantVisualConfig={smartAssistantVisualConfig}
                  onConversationUpdate={onConversationUpdate}
                  isGuest={isGuest}
                />
              </>
            )}
          </Flex>
        </Paper>
      </UpgradePlanBlurCard>
    </ManageSmartAssistantSectionControlWrapper>
  );
}
